// Pure coordinate helpers, extracted from selectregion.ts so they're unit-testable without its jQuery/Pinia graph.

export type ViewBoxRect = {x: number; y: number; w: number; h: number};

/** Parse an "x y w h" viewBox string; null if absent or malformed. */
export function parseViewBoxString(value: string | null): ViewBoxRect | null {
  if (!value) return null;
  const parts = value.trim().split(/\s+/).map(Number);
  if (parts.length !== 4 || parts.some((n) => Number.isNaN(n))) return null;
  return {x: parts[0], y: parts[1], w: parts[2], h: parts[3]};
}

/**
 * Map a selection's client rect to [left, top, right, bottom] percentages of the base
 * (full-document) viewBox via the inverse screen CTM. % of the full document is zoom/pan-invariant.
 */
export function clientRectToBasePercent(
  rect: {left: number; top: number; right: number; bottom: number},
  inverseCtm: {a: number; b: number; c: number; d: number; e: number; f: number},
  base: ViewBoxRect,
): number[] {
  const toUser = (x: number, y: number) => ({
    x: inverseCtm.a * x + inverseCtm.c * y + inverseCtm.e,
    y: inverseCtm.b * x + inverseCtm.d * y + inverseCtm.f,
  });
  const topLeft = toUser(rect.left, rect.top);
  const bottomRight = toUser(rect.right, rect.bottom);
  return [
    (100 * (topLeft.x - base.x)) / base.w,
    (100 * (topLeft.y - base.y)) / base.h,
    (100 * (bottomRight.x - base.x)) / base.w,
    (100 * (bottomRight.y - base.y)) / base.h,
  ];
}

/**
 * Map a saved selection (0..1 fractions of the base viewBox) to a box-relative pixel rect for the
 * snippet marker, via the current CTM.  Reverse of {@link clientRectToBasePercent} (which is 0–100).
 * Callers must re-run on every view change (no ancestor transform carries the marker).
 */
export function basePercentToBox(
  coords: {left: number; top: number; right: number; bottom: number},
  ctm: {a: number; b: number; c: number; d: number; e: number; f: number},
  base: ViewBoxRect,
  origin: {left: number; top: number},
): {left: number; top: number; width: number; height: number} {
  const toScreen = (fx: number, fy: number) => {
    const ux = base.x + fx * base.w;
    const uy = base.y + fy * base.h;
    return {x: ctm.a * ux + ctm.c * uy + ctm.e, y: ctm.b * ux + ctm.d * uy + ctm.f};
  };
  const tl = toScreen(coords.left, coords.top);
  const br = toScreen(coords.right, coords.bottom);
  // min corner + abs extent, so a flipped board's mirrored X still yields a positive box
  return {
    left: Math.min(tl.x, br.x) - origin.left,
    top: Math.min(tl.y, br.y) - origin.top,
    width: Math.abs(br.x - tl.x),
    height: Math.abs(br.y - tl.y),
  };
}
